import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';

const MyApplications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        if (!currentUser) {
          navigate('/login');
          return;
        }

        // First, get application IDs from user's subcollection
        const userAppsRef = collection(db, 'users', currentUser.uid, 'applications');
        const userAppsSnapshot = await getDocs(userAppsRef);
        
        const userApps = userAppsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Then fetch the most up-to-date data from the main scholarshipApplications collection
        const updatedApps = [];
        for (const app of userApps) {
          try {
            // Get the latest data from the main collection
            const appRef = doc(db, 'scholarship_applications', app.id);
            const appSnapshot = await getDoc(appRef);
            
            if (appSnapshot.exists()) {
              // Use the latest data from the main collection
              updatedApps.push({
                id: appSnapshot.id,
                ...appSnapshot.data()
              });
              console.log(`Updated app data for ${app.id}:`, appSnapshot.data());
            } else {
              // Fall back to the user's data if not found in main collection
              updatedApps.push(app);
              console.log(`Using user app data for ${app.id} (not found in main collection)`);
            }
          } catch (error) {
            console.error(`Error fetching updated data for application ${app.id}:`, error);
            // Fall back to the user's data
            updatedApps.push(app);
          }
        }
        
        // Sort by most recent first
        updatedApps.sort((a, b) => new Date(b.appliedAt) - new Date(a.appliedAt));
        setApplications(updatedApps);
      } catch (error) {
        console.error('Error fetching applications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [currentUser, navigate]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';  
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'waitlisted':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPhaseDisplay = (phase) => {
    switch (phase) {
      case 'document_review':
        return 'Document Review';
      case 'quiz':
        return 'Quiz Phase';
      case 'completed':
        return 'Completed';
      default:
        return phase ? phase.replace('_', ' ') : 'Unknown';
    }
  };

  // Format the status for display
  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
    // Format date and time
  const formatDateTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8">My Scholarship Applications</h1>

      {applications.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600 mb-4">You haven't applied for any scholarships yet.</p>
          <Link
            to="/scholarships"
            className="inline-block bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,75,15)] transition-colors duration-300"
          >
            View Available Scholarships
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {applications.map((application) => (
            <div
              key={application.id}
              className="bg-white rounded-lg shadow-md p-6 border border-gray-200"
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {application.scholarshipTitle}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Applied on {new Date(application.appliedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                      application.status
                    )}`}
                  >
                    {formatStatus(application.status)}
                  </span>
                  <span className="text-sm text-gray-600">
                    Phase: {getPhaseDisplay(application.phase)}
                  </span>
                </div>
              </div>

              {/* Admin Comments */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Admin Messages & Next Steps</h3>
                <div className="space-y-3">
                  {application.adminComments && application.adminComments.length > 0 ? (
                    application.adminComments.map((comment, index) => (
                      <div key={index} className="p-3 bg-blue-50 border border-blue-200 rounded-md shadow-sm">
                        <div className="text-sm text-gray-700 whitespace-pre-line mb-1">{comment.text}</div>
                        {/* Display admin name if available - TODO: Fetch admin user details */}
                        {comment.timestamp && (
                          <p className="text-xs text-gray-500 mt-1">
                            {/* Use formatDateTime helper */}
                            {formatDateTime(comment.timestamp)}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No comments yet.</p>
                  )}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4 mt-4">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Application Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Institution</p>
                    <p className="font-medium">{application.institution}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Department</p>
                    <p className="font-medium">{application.department}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Level</p>
                    <p className="font-medium">{application.level}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">CGPA</p>
                    <p className="font-medium">{application.cgpa}</p>
                  </div>
                </div>
              </div>

              {application.phase === 'quiz' && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">Quiz Phase</h3>
                      <p className="text-sm text-gray-600">
                        Your application has been approved for the quiz phase.
                      </p>
                    </div>
                    <Link
                      to={`/scholarships/quiz/${application.id}`}
                      className="bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,75,15)] transition-colors duration-300"
                    >
                      Take Quiz
                    </Link>
                  </div>
                </div>
              )}

              {application.quizScore !== null && (
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Quiz Results</h3>
                  <p className="text-sm text-gray-600">
                    Score: <span className="font-medium">{application.quizScore}%</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Completed on:{' '}
                    <span className="font-medium">
                      {formatDateTime(application.quizCompletedAt)}
                    </span>
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyApplications; 